// src/pages/CreateProviderAlert.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSpecialties } from "../APIs/dropdownApi";
import { getStates, getCitiesByState } from "../APIs/geoApi";
import usStates from "../data/us-states.json";
import { fetchCurrentUser } from "../APIs/authApi";
import CompletePatientProfileModal from "../Patient/CompletePatientProfileModal";
import keycloak from "../APIs/keycloak";
import { createProviderAlert } from "../APIs/providerAlerts";
import Header from "../modals/Header.js";

// ✅ Helper functions OUTSIDE the component
const getMinStartTime = (selectedDate) => {
  if (!selectedDate) return "";

  const today = new Date();
  const [y, m, d] = selectedDate.split("-").map(Number);
  const selected = new Date(y, m - 1, d);

  if (
    selected.getFullYear() === today.getFullYear() &&
    selected.getMonth() === today.getMonth() &&
    selected.getDate() === today.getDate()
  ) {
    const minHour = today.getHours() + 1;
    return `${String(minHour).padStart(2, "0")}:00`;
  }

  return "";
};

const getMinEndTime = (startTime) => {
  if (!startTime) return "";

  const [hours, minutes] = startTime.split(":").map(Number);
  const startDate = new Date();
  startDate.setHours(hours, minutes, 0, 0);
  startDate.setMinutes(startDate.getMinutes() + 5);

  const newHours = String(startDate.getHours()).padStart(2, "0");
  const newMinutes = String(startDate.getMinutes()).padStart(2, "0");

  return `${newHours}:${newMinutes}`;
};

const getMaxEndTime = () => {
  return "23:59";
};

// ✅ Single export default
export default function CreateProviderAlert({ patientId }) {
  const navigate = useNavigate();

  // Dropdown data
  const [specialties, setSpecialties] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Form state
  const [form, setForm] = useState({
    specialtyId: "",
    specialtyName: "",
    stateId: "",
    stateCode: "",
    cityId: "",
    cityName: "",
    date: "",
    startTime: "",
    endTime: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [timeWarning, setTimeWarning] = useState("");

  // Gating
  const [currentUser, setCurrentUser] = useState(null);
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);

  const token = keycloak?.token;
  const effectivePatientId = currentUser?.patientId ?? patientId ?? null;

  function toIsoWithOffset(dateStr, timeStr) {
    const [y, mo, d] = dateStr.split("-").map(Number);
    const [hh, mm] = timeStr.split(":").map(Number);
    const dt = new Date(y, mo - 1, d, hh, mm, 0);

    const offsetMin = -dt.getTimezoneOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMin);
    const offH = String(Math.floor(abs / 60)).padStart(2, "0");
    const offM = String(abs % 60).padStart(2, "0");

    const yyyy = dt.getFullYear();
    const MM = String(dt.getMonth() + 1).padStart(2, "0");
    const DD = String(dt.getDate()).padStart(2, "0");
    const HH = String(dt.getHours()).padStart(2, "0");
    const MIN = String(dt.getMinutes()).padStart(2, "0");

    return `${yyyy}-${MM}-${DD}T${HH}:${MIN}:00${sign}${offH}:${offM}`;
  }

  useEffect(() => {
    getSpecialties().then((data) => setSpecialties(data || []));

    (async () => {
      setLoadingStates(true);
      try {
        const states = await getStates();
        const opts = (states || []).map((s) => ({
          value: String(s.id),
          label: s.name,
          code:
            s.stateCode ||
            (usStates.find((u) => u.name === s.name)?.code ?? ""),
        }));
        setStateOptions(opts);
      } finally {
        setLoadingStates(false);
      }
    })();

    (async () => {
      try {
        const me = await fetchCurrentUser();
        setCurrentUser(me);
      } catch (e) {
        console.error("Failed to fetch current user for alert gating:", e);
        setCurrentUser(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (!form.stateId) {
      setCityOptions([]);
      setForm((f) => ({ ...f, cityId: "", cityName: "" }));
      return;
    }
    let on = true;
    (async () => {
      setLoadingCities(true);
      try {
        const cities = await getCitiesByState(Number(form.stateId));
        if (!on) return;
        setCityOptions(
          (cities || []).map((c) => ({ value: String(c.id), label: c.name })),
        );
      } finally {
        setLoadingCities(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [form.stateId]);

  const isValid =
    effectivePatientId &&
    form.specialtyId &&
    form.stateId &&
    form.cityId &&
    form.date &&
    form.startTime &&
    form.endTime &&
    !timeWarning;

  const onChange = (e) => {
    const { name, value } = e.target;

    if (name === "specialtyId") {
      const sel = (specialties || []).find(
        (s) => String(s.id) === String(value),
      );
      setForm((f) => ({
        ...f,
        specialtyId: value,
        specialtyName: sel?.name || "",
      }));
      return;
    }

    if (name === "stateId") {
      const sel = stateOptions.find((s) => String(s.value) === String(value));
      setForm((f) => ({
        ...f,
        stateId: value,
        stateCode: sel?.code || "",
        cityId: "",
        cityName: "",
      }));
      return;
    }

    if (name === "cityId") {
      const sel = cityOptions.find((c) => String(c.value) === String(value));
      setForm((f) => ({ ...f, cityId: value, cityName: sel?.label || "" }));
      return;
    }

    if (name === "date") {
      setForm((f) => ({ ...f, date: value, startTime: "", endTime: "" }));
      setTimeWarning("");
      return;
    }

    if (name === "startTime") {
      setForm((f) => ({ ...f, startTime: value, endTime: "" }));
      setTimeWarning("");
      return;
    }

    if (name === "endTime") {
      setForm((f) => ({ ...f, endTime: value }));

      if (form.startTime && value) {
        const [startH, startM] = form.startTime.split(":").map(Number);
        const [endH, endM] = value.split(":").map(Number);

        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        const diff = endMinutes - startMinutes;

        if (diff < 5) {
          setTimeWarning(
            "End time must be at least 5 minutes after start time.",
          );
        } else if (endMinutes > 23 * 60 + 59) {
          setTimeWarning("End time must be on the same day (before midnight).");
        } else {
          setTimeWarning("");
        }
      }
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  const todayDate = (() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  })();

  const handleCreateAlertCore = async () => {
    if (!isValid) {
      alert("Please complete all required fields.");
      return;
    }

    if (!effectivePatientId) {
      alert(
        "Unable to determine patient account. Please refresh and try again.",
      );
      return;
    }

    setSubmitting(true);
    setSuccessMsg("");

    try {
      const startIso = toIsoWithOffset(form.date, form.startTime);
      const endIso = toIsoWithOffset(form.date, form.endTime);

      if (new Date(startIso) >= new Date(endIso)) {
        alert("End time must be after start time.");
        return;
      }

      const payload = {
        specialty: form.specialtyName,
        city: form.cityName,
        state: form.stateCode,
        start_date_time: startIso,
        end_date_time: endIso,
      };

      await createProviderAlert(payload);
      setSuccessMsg("Alert created successfully.");
      navigate(`/patient/${effectivePatientId}/profile?tab=alerts`);
    } catch (err) {
      console.error(err);
      alert("Failed to create alert. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    if (!keycloak?.authenticated) {
      alert("Please sign up or sign in to create alerts.");
      return;
    }

    if (!currentUser) {
      alert("Loading your account, please try again in a moment.");
      return;
    }

    if (currentUser.accountPhase === "BASIC_ACCOUNT") {
      setShowCompleteProfileModal(true);
      return;
    }

    await handleCreateAlertCore();
  };

  return (
    <>
      <CompletePatientProfileModal
        isOpen={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
        token={token}
        currentUser={currentUser}
        onCompleted={async () => {
          setCurrentUser((prev) =>
            prev ? { ...prev, accountPhase: "COMPLETED_PROFILE" } : prev,
          );
          setShowCompleteProfileModal(false);
          await new Promise((resolve) => setTimeout(resolve, 500));
          try {
            await keycloak.updateToken(30);
          } catch (err) {
            console.error("Failed to refresh token:", err);
          }
          await handleCreateAlertCore();
        }}
      />

      <div className="min-h-screen bg-gray-50">
        <Header />

        <div className="max-w-2xl mx-auto px-6 py-8">
          {/* Back */}
          <button
            onClick={() => window.history.back()}
            className="mb-6 text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 transition-colors"
          >
            ← Back
          </button>

          {/* Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-teal-500 pl-3 mb-8">
              Create Provider Alert
            </h2>

            <form onSubmit={onSubmit} className="space-y-5">
              {/* Specialty */}
              <div className="grid grid-cols-[10rem_1fr] items-center gap-4">
                <label
                  htmlFor="specialtyId"
                  className="text-sm font-semibold text-gray-700"
                >
                  Specialty
                </label>
                <select
                  id="specialtyId"
                  name="specialtyId"
                  value={form.specialtyId}
                  onChange={onChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                >
                  <option value="">Select specialty</option>
                  {(specialties || []).map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* State */}
              <div className="grid grid-cols-[10rem_1fr] items-center gap-4">
                <label
                  htmlFor="stateId"
                  className="text-sm font-semibold text-gray-700"
                >
                  State
                </label>
                <select
                  id="stateId"
                  name="stateId"
                  value={form.stateId}
                  onChange={onChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                >
                  <option value="">
                    {loadingStates ? "Loading states…" : "Select state"}
                  </option>
                  {stateOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div className="grid grid-cols-[10rem_1fr] items-center gap-4">
                <label
                  htmlFor="cityId"
                  className="text-sm font-semibold text-gray-700"
                >
                  City
                </label>
                <select
                  id="cityId"
                  name="cityId"
                  value={form.cityId}
                  onChange={onChange}
                  disabled={!form.stateId || loadingCities}
                  className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 ${
                    !form.stateId ? "bg-gray-50 text-gray-400" : ""
                  }`}
                >
                  <option value="">
                    {!form.stateId
                      ? "Select state first"
                      : loadingCities
                        ? "Loading cities…"
                        : "Select city"}
                  </option>
                  {cityOptions.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Availability Date */}
              <div className="grid grid-cols-[10rem_1fr] items-center gap-4">
                <label
                  htmlFor="date"
                  className="text-sm font-semibold text-gray-700"
                >
                  Availability Date
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={form.date}
                  onChange={onChange}
                  min={todayDate}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                />
              </div>

              {/* Availability Time */}
              <div className="grid grid-cols-[10rem_1fr] items-start gap-4">
                <label className="text-sm font-semibold text-gray-700 pt-2">
                  Availability Time
                </label>
                <div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="time"
                        id="startTime"
                        name="startTime"
                        value={form.startTime}
                        onChange={onChange}
                        min={getMinStartTime(form.date)}
                        disabled={!form.date}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 ${
                          !form.date ? "bg-gray-50 text-gray-400" : ""
                        }`}
                      />
                      <p className="text-xs text-gray-500 mt-1">Start time</p>
                    </div>
                    <div>
                      <input
                        type="time"
                        id="endTime"
                        name="endTime"
                        value={form.endTime}
                        onChange={onChange}
                        min={getMinEndTime(form.startTime)}
                        max={getMaxEndTime()}
                        disabled={!form.startTime}
                        className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 ${
                          !form.startTime ? "bg-gray-50 text-gray-400" : ""
                        }`}
                      />
                      <p className="text-xs text-gray-500 mt-1">End time</p>
                    </div>
                  </div>

                  {timeWarning && (
                    <p className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-sm mt-2">
                      ⚠️ {timeWarning}
                    </p>
                  )}

                  {form.startTime && !form.endTime && !timeWarning && (
                    <p className="text-teal-600 text-sm mt-2">
                      End time must be at least 5 minutes after start time and
                      on the same day.
                    </p>
                  )}
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={submitting || !isValid}
                  className={`w-full py-2.5 rounded-full font-semibold text-sm transition-colors ${
                    submitting || !isValid
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-teal-600 hover:bg-teal-700 text-white"
                  }`}
                >
                  {submitting ? "Creating..." : "Create Alert"}
                </button>

                {!isValid && (
                  <p className="text-red-600 text-sm mt-2 text-center">
                    Complete all fields to create an alert.
                  </p>
                )}

                {successMsg && (
                  <p className="text-teal-600 text-sm mt-3 text-center">
                    {successMsg}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
